import React from 'react'
import { ChevronRight } from 'lucide-react'
import { mergeClasses } from '../../helpers/styling'
import { SvgIcon } from '../svg-icon'
import { RiskVerdict } from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { STATUS_STYLE, Tone } from './status-style'

/**
 * Severity, except where the finding is what put a risk category into "confirm this" — then it wears that
 * instead. An observation labelled INFO under an amber risk row is the same fact told twice in two colours.
 */
export function SeverityPill(props: { tone: Tone }) {
  const style = STATUS_STYLE.tone[props.tone]
  return (
    <span
      className={mergeClasses(
        'rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        style.pill
      )}
    >
      {style.label}
    </span>
  )
}

/** Green when the check passed, amber when it found something to confirm, red when it found a violation. */
export function RowStatusIcon(props: { fired: boolean; review: boolean }) {
  const status = !props.fired ? 'clear' : props.review ? 'review' : 'flagged'
  const style = STATUS_STYLE.risk[status]
  return <SvgIcon icon={style.icon} size={12} className={mergeClasses('shrink-0', style.iconColor)} />
}

/** Three states, three phrases. Nothing else on screen says what the colours mean. */
export function StatusLegend() {
  const entries = [
    ['clear', 'passed check'],
    ['review', 'check manually if this is fine'],
    ['flagged', 'in violation of information security'],
  ] as const
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground">
      <span className="font-medium">Legend:</span>
      {entries.map(([status, label]) => (
        <span key={status} className="flex items-center gap-1">
          <SvgIcon icon={STATUS_STYLE.risk[status].icon} size={12} className={STATUS_STYLE.risk[status].iconColor} />{' '}
          {label}
        </span>
      ))}
    </div>
  )
}

/**
 * The summary row of a risk accordion, in the app verdict and in each widget alike: the verdict, why, and
 * how many of its checks found something. "0/1" reads as a score against an unknown total, so it says what
 * was counted.
 */
export function RiskSummaryRow(props: { risk: RiskVerdict; fired: number; watched: number }) {
  const style = STATUS_STYLE.risk[props.risk.status]
  return (
    <summary className="flex cursor-pointer list-none items-start gap-2 px-3 py-2 hover:bg-muted/50">
      <SvgIcon
        icon={ChevronRight}
        size={14}
        className="mt-0.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
      />
      <SvgIcon icon={style.icon} size={16} className={mergeClasses('mt-0.5 shrink-0', style.iconColor)} />
      {/* Wraps as a pair: in a narrow panel the count drops below the verdict rather than squeezing it. */}
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="min-w-0 flex-1 basis-60">
          <span className={mergeClasses('font-medium', style.text)}>{props.risk.label}</span>
          <span className="text-muted-foreground">
            {' — '}
            {props.risk.note}
          </span>
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {props.fired > 0
            ? `${props.fired} of ${props.watched} checks found something`
            : `${props.watched} ${props.watched === 1 ? 'check' : 'checks'}, nothing found`}
        </span>
      </span>
    </summary>
  )
}
